import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

export type StudioFilterField = {
  id: string;
  label: string;
  kind?: "text" | "select";
  placeholder?: string;
  value: any;
  options?: { label: any; value: any }[];
  disabled?: boolean;
  onChange: (value: any) => void;
};

export const readFilterSelect = (value: any): string => {
  if (value == null || value === "") {
    return "";
  }
  if (typeof value === "object") {
    return value.value == null ? "" : String(value.value);
  }
  return String(value);
};

export const filterHasValue = (value: any) => {
  if (value == null || value === "") {
    return false;
  }
  if (typeof value === "object") {
    return Boolean(value.value || value.label);
  }
  return true;
};

const displayValue = (value: any) => {
  if (value == null || value === "") {
    return "";
  }
  if (typeof value === "object") {
    const label = value.label;
    if (label == null) {
      return String(value.value || "");
    }
    if (typeof label === "string" || typeof label === "number") {
      return String(label);
    }
    return String(value.value || "");
  }
  return String(value);
};

const emptyFor = (field: StudioFilterField) =>
  field.kind === "select" ? "" : "";

type StudioFilterBarProps = {
  fields: StudioFilterField[];
  defaultFieldId?: string;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onReset: () => void;
  addTo?: string;
  onAdd?: () => void;
  addAriaLabel?: string;
  /** Visible text next to the + icon, so the add action is clear on phones. */
  addLabel?: string;
  hideAdd?: boolean;
  moreContent?: React.ReactNode;
  moreCount?: number;
  trailing?: React.ReactNode;
  leading?: React.ReactNode;
  wideToolbar: React.ReactNode;
};

const AddControl = ({
  addTo,
  onAdd,
  addAriaLabel,
  addLabel,
}: {
  addTo?: string;
  onAdd?: () => void;
  addAriaLabel: string;
  addLabel?: string;
}) => {
  const className = `hkp-filter-bar__iconbtn is-primary${addLabel ? " has-label" : ""}`;
  const icon = (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <path
        d="M10 4.5v11M4.5 10h11"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
  if (addTo) {
    return (
      <Link className={className} to={addTo} aria-label={addAriaLabel}>
        {icon}
        {addLabel ? <span className="hkp-filter-bar__addlabel">{addLabel}</span> : null}
      </Link>
    );
  }
  return (
    <button
      type="button"
      className={className}
      aria-label={addAriaLabel}
      onClick={onAdd}
    >
      {icon}
      {addLabel ? <span className="hkp-filter-bar__addlabel">{addLabel}</span> : null}
    </button>
  );
};

const StudioFilterBar = (props: StudioFilterBarProps) => {
  const {
    fields,
    defaultFieldId,
    onKeyDown,
    onReset,
    addTo,
    onAdd,
    addAriaLabel = "Add",
    addLabel,
    hideAdd,
    moreContent,
    moreCount = 0,
    trailing,
    leading,
    wideToolbar,
  } = props;
  const initialId = defaultFieldId && fields.some((f) => f.id === defaultFieldId)
    ? defaultFieldId
    : fields[0]?.id;
  const [fieldId, setFieldId] = useState(initialId || "");
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    if (fieldId && fields.some((f) => f.id === fieldId)) {
      return;
    }
    if (fields[0]?.id) {
      setFieldId(fields[0].id);
    }
  }, [fields, fieldId]);

  const activeField = fields.find((f) => f.id === fieldId) || fields[0];
  const chips = useMemo(
    () =>
      fields.filter((field) => field.id !== activeField?.id && filterHasValue(field.value)),
    [fields, activeField]
  );

  useEffect(() => {
    if (!moreOpen) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMoreOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.body.classList.add("hkp-filter-sheet-open");
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("hkp-filter-sheet-open");
    };
  }, [moreOpen]);

  const showAdd = !hideAdd && (addTo || onAdd);
  const compactValue =
    activeField?.kind === "select"
      ? displayValue(activeField.value)
      : displayValue(activeField?.value);

  const sheet =
    moreOpen && moreContent && typeof document !== "undefined"
      ? createPortal(
          <div className="hkp-filter-sheet" role="dialog" aria-modal="true" aria-label="More filters">
            <button
              type="button"
              className="hkp-filter-sheet__backdrop"
              aria-label="Close filters"
              onClick={() => setMoreOpen(false)}
            />
            <div className="hkp-filter-sheet__panel">
              <div className="hkp-filter-sheet__head">
                <h2>Filters</h2>
                <button
                  type="button"
                  className="hkp-filter-bar__iconbtn"
                  aria-label="Close filters"
                  onClick={() => setMoreOpen(false)}
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <div className="hkp-filter-sheet__body">{moreContent}</div>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div className="hkp-filter-bar">
      <div className="hkp-filter-bar__compact">
        {leading ? <div className="hkp-filter-bar__leading">{leading}</div> : null}
        {fields.length > 1 ? (
          <label className="hkp-filter-bar__field">
            <span className="visually-hidden">Filter by</span>
            <select
              value={activeField?.id || ""}
              onChange={(e) => setFieldId(e.target.value)}
              aria-label="Filter field"
            >
              {fields.map((field) => (
                <option key={field.id} value={field.id}>
                  {field.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {activeField ? (
        <div
          className={`hkp-filter-bar__value${fields.length > 1 ? "" : " hkp-filter-bar__value--solo"}`}
        >
          {activeField?.kind === "select" ? (
            <select
              aria-label={activeField.label}
              disabled={activeField.disabled}
              value={
                typeof activeField.value === "object"
                  ? activeField.value?.value ?? ""
                  : activeField.value ?? ""
              }
              onChange={(e) => {
                const next = e.target.value;
                const match = (activeField.options || []).find(
                  (opt) => String(opt.value) === next
                );
                activeField.onChange(match || (next ? { label: next, value: next } : ""));
              }}
            >
              <option value="">{activeField.placeholder || "Any"}</option>
              {(activeField.options || []).map((opt) => (
                <option key={String(opt.value)} value={opt.value}>
                  {typeof opt.label === "string" || typeof opt.label === "number"
                    ? String(opt.label)
                    : String(opt.value)}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="search"
              enterKeyHint="search"
              autoComplete="off"
              disabled={activeField?.disabled}
              placeholder={activeField?.placeholder || activeField?.label || "Search"}
              value={compactValue}
              aria-label={activeField?.label || "Search"}
              onChange={(e) => activeField?.onChange(e.target.value)}
              onKeyDown={onKeyDown}
            />
          )}
        </div>
        ) : null}
        {moreContent ? (
          <button
            type="button"
            className={`hkp-filter-bar__iconbtn${moreOpen ? " is-on" : ""}`}
            aria-label={moreCount ? `More filters, ${moreCount} active` : "More filters"}
            onClick={() => setMoreOpen((open) => !open)}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
              <path
                d="M3 5h14M5.5 10h9M8 15h4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
            {moreCount ? <span className="hkp-filter-bar__badge">{moreCount}</span> : null}
          </button>
        ) : null}
        <button
          type="button"
          className="hkp-filter-bar__iconbtn"
          aria-label="Reset filters"
          onClick={() => {
            setMoreOpen(false);
            onReset();
          }}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
            <path
              d="M4.5 10A5.5 5.5 0 1 0 6 6.2"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
            <path
              d="M4 3.5v3.2h3.2"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        {showAdd ? (
          <AddControl addTo={addTo} onAdd={onAdd} addAriaLabel={addAriaLabel} addLabel={addLabel} />
        ) : null}
        {trailing ? <div className="hkp-filter-bar__trailing">{trailing}</div> : null}
      </div>
      {chips.length ? (
        <div className="hkp-filter-bar__chips" aria-label="Active filters">
          {chips.map((field) => (
            <button
              key={field.id}
              type="button"
              className="hkp-filter-bar__chip"
              onClick={() => field.onChange(emptyFor(field))}
              aria-label={`Clear ${field.label} filter`}
            >
              <span>
                {field.label}: {displayValue(field.value)}
              </span>
              <span aria-hidden="true">×</span>
            </button>
          ))}
        </div>
      ) : null}
      <div className="hkp-filter-bar__wide">{wideToolbar}</div>
      {sheet}
    </div>
  );
};

export default StudioFilterBar;
